import { messageKey, useI18n, type MessageKey } from "@baton/i18n";
import { LogOut, Monitor, Moon, Sun, User } from "@baton/icons";
import { Avatar, Popover, SegmentedControl, useTheme } from "@baton/ui";
import { useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import type { MeView } from "../../app/queries.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { signOut } from "../../auth/session/signOut.ts";
import { accountSettingsPath } from "../../settings/profile/paths.ts";
import { NavCardRow } from "./NavCardRow.tsx";

/**
 * FR-015-002, FR-015-100, FR-015-130, `contracts/screens.md` § Sidebar (Footer), `contracts/design-system.md`
 * § The shell and § Names, roles and structure: the account card, in the same place on every
 * authenticated route so help is consistent (SC 3.2.6). The card is Spectrum UI's Profile Dropdown
 * (`@spectrumui/profile-dropdown`, Apache-2.0), themed through the variables of `packages/tokens`
 * (2026-09-15, by the owner's decision): the actor's avatar, name and email under one rule; the theme
 * as the source's segmented control, Light, Dark and System, applied at once and persisted as
 * FR-015-130 states; then the rows, Profile and Sign out in the danger tone (2026-09-17, by the
 * owner's decision: Keyboard shortcuts left the card for the rail's one tile, so it is offered once).
 * The theme is chosen here and nowhere else (2026-09-14, by the owner's instruction; Preferences' copy of
 * it went on 2026-10-03, by the owner's approval of the product review). Profile opens the person's
 * Profile page in the settings of the Team they are in, or in their own frame outside any Team.
 * The sidebar's footer opens it above the actor row; while the sidebar is collapsed the rail's
 * avatar tile opens it beside the rail (2026-09-17), the one card from two triggers.
 *
 * What differs from the source, and why: the theme control is a radio group over Radix, so the
 * arrow keys move the choice and a screen reader hears one choice of three, and it carries System,
 * which the source's two buttons have no room for; the source's wallet card, its upgrade action and
 * its brand footer have no counterpart in Baton and are not drawn; the rows are the Nav List Card
 * rows the workspace switcher uses (`NavCardRow.tsx`), so the chrome's two cards move alike; and the
 * card opens in a popover rather than expanding in place, because its triggers are pinned.
 */
const THEMES: readonly { readonly value: "light" | "dark" | "system"; readonly label: MessageKey; readonly icon: ReactNode }[] = [
  { value: "light", label: messageKey("shell.theme.light"), icon: <Sun /> },
  { value: "dark", label: messageKey("shell.theme.dark"), icon: <Moon /> },
  { value: "system", label: messageKey("shell.theme.system"), icon: <Monitor /> },
];

export interface AccountCardProps {
  readonly me: MeView;
  readonly placement: "top" | "right";
  readonly align: "start" | "end";
  /** The trigger, which names the card through `label`. */
  readonly trigger: (label: string) => ReactNode;
  /** The card of a client member's portal shell (028), whose Team the address names but who belongs to none: Profile opens their own frame. */
  readonly outsideTeam?: boolean;
}

export function actorOf(me: MeView) {
  return { id: me.id, kind: me.kind === "agent" ? "agent" : "user", display_name: me.display_name, avatar_url: null } as const;
}

export function AccountCard({ me, placement, align, trigger, outsideTeam = false }: AccountCardProps) {
  const { t } = useI18n();
  const runtime = useRuntime();
  const navigate = useNavigate();
  const { ws = null } = useParams();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const label = t("nav.accountMenu", { name: me.display_name });
  const act = (run: () => void) => () => {
    setOpen(false);
    run();
  };
  return (
    <Popover open={open} onOpenChange={setOpen} placement={placement} align={align} label={label} className="w-(--sidebar-width) overflow-hidden p-0" trigger={trigger(label)}>
      <div className="flex items-center gap-(--space-3) border-b border-border-subtle p-(--space-4)">
        <Avatar actor={actorOf(me)} size="lg" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-text-primary">{me.display_name}</span>
          <span className="truncate text-caption text-text-tertiary">{me.email}</span>
        </div>
      </div>
      <div className="border-b border-border-subtle p-(--space-3)">
        <SegmentedControl className="flex w-full" label={t("shell.theme.label")} value={theme.theme} options={THEMES.map((choice) => ({ value: choice.value, label: t(choice.label), icon: choice.icon }))} onValueChange={theme.setTheme} />
      </div>
      <ul className="m-0 flex list-none flex-col gap-(--space-1) p-(--space-2)">
        <NavCardRow icon={<User />} label={t("nav.profile")} onSelect={act(() => { void navigate(accountSettingsPath(outsideTeam ? null : ws, "profile")); })} />
        <NavCardRow icon={<LogOut />} label={t("nav.signOut")} tone="danger" onSelect={act(() => { void signOut(runtime); })} />
      </ul>
    </Popover>
  );
}
